import axios from 'axios';

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

const getAccessToken = () => sessionStorage.getItem('token');
const getUUID = () => sessionStorage.getItem('uuid');
const getUserId = () => sessionStorage.getItem('userId');

const refreshAccessToken = async () => {
    try {
        const response = await axiosInstance.get(`/user/refresh-token`);
        return response.headers;
    } catch (error) {
        throw error;
    }
};

// Request interceptor to attach auth token to requests
axiosInstance.interceptors.request.use(
    (config) => {
        const token = getAccessToken();
        const uuid = getUUID();
        const userId = getUserId();

        if (token) {
            config.headers['Authorization'] = `Bearer ${JSON.parse(token)}`;
        }

        if (uuid) {
            config.headers['uuid'] = uuid;
        }

        if (userId) {
            config.headers['userId'] = userId;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh
let isRefreshing = false;
let failedQueue = [];

axiosInstance.interceptors.response.use(
    (response) => response, // Simply return the response if no error
    async (error) => {
        const originalRequest = error.config;

        if (error.response.status === 409 && error.response.data.message === "User Logged In from Another Browser") {
            try {
                // const res = await axiosInstance.get('/user/logout');
            } catch (err) {
                console.log(err.response.data.message || 'Something went wrong.');
            } finally {
                sessionStorage.clear();
                window.location.href = '/login';
            }
        }

        // Handle 401 errors
        if ((error.response && error.response.status === 401 && error.response.data.message === "Jwt token is Expired" && !originalRequest._retry) || error.response.data.message.includes('No static resource')) {
            // Prevent multiple requests for token refresh
            if (isRefreshing) {
                // If a refresh is already in progress, add the request to the queue
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((data) => data)
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Attempt to refresh the access token
                const data = await refreshAccessToken();
                // Save the new access token (you can also save it in Redux)
                sessionStorage.setItem('token', JSON.stringify(data?.token));
                sessionStorage.setItem('expiryDate', data?.epochtime);

                // Retry the original request with the new access token
                axiosInstance.defaults.headers['Authorization'] = `Bearer ${JSON.parse(data.token)}`;
                originalRequest.headers['Authorization'] = `Bearer ${JSON.parse(data.token)}`;

                // Retry all failed requests in the queue with the new token
                failedQueue.forEach((promise) => promise.resolve(data));
                failedQueue = [];

                // Return the original request's response
                return axiosInstance(originalRequest);
            } catch (err) {
                // If refreshing the token fails, log the user out
                failedQueue.forEach((promise) => promise.reject(err));
                failedQueue = [];
                // Clear tokens and redirect to login page (or any other error handling)
                sessionStorage.clear();
                window.location.href = '/login';  // Redirect to login page (adjust as necessary)
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }

        // If not a 401 error, just reject the promise
        return Promise.reject(error);
    }
);

export default axiosInstance;
